import { api } from '../api/client';

import { API_SHORT_LINKS_URL } from '../utils';
import { withElementChoicesField } from '../utils/elementRefusal';

/**
 * Short links — `${origin}/s/{code}`, resolved by the /s/:code route.
 *
 * Two kinds:
 *   filter — a stored query against one collection. The API REQUIRES a
 *            non-empty query that parses and compiles, so an unfiltered view
 *            is shared as a `path` link instead (see createFilterShortLink).
 *   path   — the generic tiny-url, in-app paths only.
 *
 * `url` comes back null when the API has no APP_BASE_URL configured; the code
 * is always returned, so callers fall back to buildShortLinkUrl(code).
 */

export async function createShortLink(payload) {
	const { data } = await api.post(API_SHORT_LINKS_URL, payload);
	return data;
}

/**
 * A short link for the current items view. With a query it is a `filter`
 * link (the compact, QR-able point of the feature); with none there is
 * nothing to compact and no query for the API to validate, so the plain list
 * is shared as a `path` link carrying the same sort.
 */
export function createFilterShortLink({ collection_id, query, sort_field, sort_direction, group_by, review, asPath = false, path, elementChoices = null }) {
	// A filter link stores query, sort and period and nothing else, so a view
	// narrowed by the review filter — or anything else a filter link cannot
	// carry (`asPath`: the merged list turned off) — goes as the path it is
	// (the caller puts `q`, `review`, `linked` in it).
	if (query && !review && !asPath) {
		const payload = { kind: 'filter', collection_id, query };
		if (sort_field) payload.sort_field = sort_field;
		if (sort_direction) payload.sort_direction = sort_direction;
		// The grid's separator period (utils/itemGroups). The API refuses '',
		// so an absent one is left out rather than sent empty.
		if (group_by) payload.group_by = group_by;
		// The person's answer to an ambiguous element name (utils/elementRefusal.js).
		return createShortLink(withElementChoicesField(payload, elementChoices));
	}
	return createShortLink({ kind: 'path', path });
}

/** The navigation descriptor for a code (authenticated). Unknown code → 404. */
export async function resolveShortLink(code) {
	const { data } = await api.get(`${API_SHORT_LINKS_URL}/${encodeURIComponent(code)}`);
	return data;
}
